// Comprehensive Rate Library for M&J Evans Construction

export interface PipeRates {
  excavation: number;
  pipe: number;
  bedding: number;
  backfill: number;
}

export interface ManholeRates {
  [depth: string]: number;
}

export interface RoadRates {
  surfacing: {
    [type: string]: number; // per m² per mm thickness
  };
  base: {
    [type: string]: number; // per m³
  };
  excavation: number; // per m³
  cartAway: number; // per m³
  topsoilStrip: number; // per m²
}

export interface RegionalMultipliers {
  labour: number;
  material: number;
}

export const rateLibrary = {
  // Sewer pipe rates by material, diameter, and depth category
  sewers: {
    clay: {
      "150mm": {
        "1.00": { excavation: 12.5, pipe: 15.5, bedding: 8.2, backfill: 18.5 },
        "1.25": { excavation: 14.2, pipe: 15.5, bedding: 8.2, backfill: 23.1 },
        "1.50": { excavation: 16.8, pipe: 15.5, bedding: 8.2, backfill: 27.8 },
        "1.75": { excavation: 19.4, pipe: 15.5, bedding: 8.2, backfill: 32.5 },
        "2.00": { excavation: 22.0, pipe: 15.5, bedding: 8.2, backfill: 37.2 },
        "2.25": { excavation: 24.6, pipe: 15.5, bedding: 8.2, backfill: 41.9 },
        "2.50": { excavation: 27.2, pipe: 15.5, bedding: 8.2, backfill: 46.6 },
        "2.75": { excavation: 29.8, pipe: 15.5, bedding: 8.2, backfill: 51.3 },
        "3.00": { excavation: 32.4, pipe: 15.5, bedding: 8.2, backfill: 56.0 },
      },
      "225mm": {
        "1.00": { excavation: 13.2, pipe: 22.5, bedding: 9.8, backfill: 19.2 },
        "1.25": { excavation: 15.1, pipe: 22.5, bedding: 9.8, backfill: 24.0 },
        "1.50": { excavation: 17.8, pipe: 22.5, bedding: 9.8, backfill: 28.8 },
        "1.75": { excavation: 20.5, pipe: 22.5, bedding: 9.8, backfill: 33.6 },
        "2.00": { excavation: 23.2, pipe: 22.5, bedding: 9.8, backfill: 38.4 },
        "2.25": { excavation: 25.9, pipe: 22.5, bedding: 9.8, backfill: 43.2 },
        "2.50": { excavation: 28.6, pipe: 22.5, bedding: 9.8, backfill: 48.0 },
        "2.75": { excavation: 31.3, pipe: 22.5, bedding: 9.8, backfill: 52.8 },
        "3.00": { excavation: 34.0, pipe: 22.5, bedding: 9.8, backfill: 57.6 },
      },
      "300mm": {
        "1.00": { excavation: 14.5, pipe: 32.0, bedding: 12.5, backfill: 20.5 },
        "1.25": { excavation: 16.8, pipe: 32.0, bedding: 12.5, backfill: 25.6 },
        "1.50": { excavation: 19.2, pipe: 32.0, bedding: 12.5, backfill: 30.7 },
        "1.75": { excavation: 21.6, pipe: 32.0, bedding: 12.5, backfill: 35.8 },
        "2.00": { excavation: 24.0, pipe: 32.0, bedding: 12.5, backfill: 40.9 },
        "2.25": { excavation: 26.4, pipe: 32.0, bedding: 12.5, backfill: 46.0 },
        "2.50": { excavation: 28.8, pipe: 32.0, bedding: 12.5, backfill: 51.1 },
        "2.75": { excavation: 31.2, pipe: 32.0, bedding: 12.5, backfill: 56.2 },
        "3.00": { excavation: 33.6, pipe: 32.0, bedding: 12.5, backfill: 61.3 },
      },
      "450mm": {
        "1.00": { excavation: 16.2, pipe: 58.5, bedding: 18.0, backfill: 22.8 },
        "1.25": { excavation: 18.9, pipe: 58.5, bedding: 18.0, backfill: 28.5 },
        "1.50": { excavation: 21.6, pipe: 58.5, bedding: 18.0, backfill: 34.2 },
        "1.75": { excavation: 24.3, pipe: 58.5, bedding: 18.0, backfill: 39.9 },
        "2.00": { excavation: 27.0, pipe: 58.5, bedding: 18.0, backfill: 45.6 },
        "2.25": { excavation: 29.7, pipe: 58.5, bedding: 18.0, backfill: 51.3 },
        "2.50": { excavation: 32.4, pipe: 58.5, bedding: 18.0, backfill: 57.0 },
        "2.75": { excavation: 35.1, pipe: 58.5, bedding: 18.0, backfill: 62.7 },
        "3.00": { excavation: 37.8, pipe: 58.5, bedding: 18.0, backfill: 68.4 },
      },
      "600mm": {
        "1.00": { excavation: 18.5, pipe: 85.0, bedding: 25.0, backfill: 26.2 },
        "1.25": { excavation: 21.6, pipe: 85.0, bedding: 25.0, backfill: 32.8 },
        "1.50": { excavation: 24.7, pipe: 85.0, bedding: 25.0, backfill: 39.4 },
        "1.75": { excavation: 27.8, pipe: 85.0, bedding: 25.0, backfill: 46.0 },
        "2.00": { excavation: 30.9, pipe: 85.0, bedding: 25.0, backfill: 52.6 },
        "2.25": { excavation: 34.0, pipe: 85.0, bedding: 25.0, backfill: 59.2 },
        "2.50": { excavation: 37.1, pipe: 85.0, bedding: 25.0, backfill: 65.8 },
        "2.75": { excavation: 40.2, pipe: 85.0, bedding: 25.0, backfill: 72.4 },
        "3.00": { excavation: 43.3, pipe: 85.0, bedding: 25.0, backfill: 79.0 },
      },
    },
    plastic: {}, // Will be calculated as 10% less than clay
    concrete: {}, // Will be calculated as 15% more than clay
  } as any,

  // Storm sewer rates (larger diameters)
  stormSewers: {
    plastic: {
      "750mm": {
        "1.00": {
          excavation: 22.5,
          pipe: 125.0,
          bedding: 32.0,
          backfill: 31.2,
        },
        "1.25": {
          excavation: 26.2,
          pipe: 125.0,
          bedding: 32.0,
          backfill: 39.0,
        },
        "1.50": {
          excavation: 29.9,
          pipe: 125.0,
          bedding: 32.0,
          backfill: 46.8,
        },
        "1.75": {
          excavation: 33.6,
          pipe: 125.0,
          bedding: 32.0,
          backfill: 54.6,
        },
        "2.00": {
          excavation: 37.3,
          pipe: 125.0,
          bedding: 32.0,
          backfill: 62.4,
        },
        "2.25": {
          excavation: 41.0,
          pipe: 125.0,
          bedding: 32.0,
          backfill: 70.2,
        },
        "2.50": {
          excavation: 44.7,
          pipe: 125.0,
          bedding: 32.0,
          backfill: 78.0,
        },
        "2.75": {
          excavation: 48.4,
          pipe: 125.0,
          bedding: 32.0,
          backfill: 85.8,
        },
        "3.00": {
          excavation: 52.1,
          pipe: 125.0,
          bedding: 32.0,
          backfill: 93.6,
        },
      },
      "900mm": {
        "1.00": {
          excavation: 26.8,
          pipe: 185.0,
          bedding: 42.0,
          backfill: 37.2,
        },
        "1.25": {
          excavation: 31.2,
          pipe: 185.0,
          bedding: 42.0,
          backfill: 46.5,
        },
        "1.50": {
          excavation: 35.6,
          pipe: 185.0,
          bedding: 42.0,
          backfill: 55.8,
        },
        "1.75": {
          excavation: 40.0,
          pipe: 185.0,
          bedding: 42.0,
          backfill: 65.1,
        },
        "2.00": {
          excavation: 44.4,
          pipe: 185.0,
          bedding: 42.0,
          backfill: 74.4,
        },
        "2.25": {
          excavation: 48.8,
          pipe: 185.0,
          bedding: 42.0,
          backfill: 83.7,
        },
        "2.50": {
          excavation: 53.2,
          pipe: 185.0,
          bedding: 42.0,
          backfill: 93.0,
        },
        "2.75": {
          excavation: 57.6,
          pipe: 185.0,
          bedding: 42.0,
          backfill: 102.3,
        },
        "3.00": {
          excavation: 62.0,
          pipe: 185.0,
          bedding: 42.0,
          backfill: 111.6,
        },
      },
      "1200mm": {
        "1.00": {
          excavation: 35.2,
          pipe: 285.0,
          bedding: 58.0,
          backfill: 48.6,
        },
        "1.25": {
          excavation: 41.0,
          pipe: 285.0,
          bedding: 58.0,
          backfill: 60.75,
        },
        "1.50": {
          excavation: 46.8,
          pipe: 285.0,
          bedding: 58.0,
          backfill: 72.9,
        },
        "1.75": {
          excavation: 52.6,
          pipe: 285.0,
          bedding: 58.0,
          backfill: 85.05,
        },
        "2.00": {
          excavation: 58.4,
          pipe: 285.0,
          bedding: 58.0,
          backfill: 97.2,
        },
        "2.25": {
          excavation: 64.2,
          pipe: 285.0,
          bedding: 58.0,
          backfill: 109.35,
        },
        "2.50": {
          excavation: 70.0,
          pipe: 285.0,
          bedding: 58.0,
          backfill: 121.5,
        },
        "2.75": {
          excavation: 75.8,
          pipe: 285.0,
          bedding: 58.0,
          backfill: 133.65,
        },
        "3.00": {
          excavation: 81.6,
          pipe: 285.0,
          bedding: 58.0,
          backfill: 145.8,
        },
      },
    },
  },

  // Manhole rates by diameter and depth
  manholes: {
    "900mm": {
      "1.00": 485.0,
      "1.50": 625.0,
      "2.00": 785.0,
      "2.50": 945.0,
      "3.00": 1105.0,
      "3.50": 1265.0,
      "4.00": 1425.0,
    },
    "1050mm": {
      "1.00": 545.0,
      "1.50": 705.0,
      "2.00": 885.0,
      "2.50": 1065.0,
      "3.00": 1245.0,
      "3.50": 1425.0,
      "4.00": 1605.0,
    },
    "1200mm": {
      "1.00": 615.0,
      "1.50": 795.0,
      "2.00": 995.0,
      "2.50": 1195.0,
      "3.00": 1395.0,
      "3.50": 1595.0,
      "4.00": 1795.0,
    },
    "1350mm": {
      "1.00": 685.0,
      "1.50": 885.0,
      "2.00": 1105.0,
      "2.50": 1325.0,
      "3.00": 1545.0,
      "3.50": 1765.0,
      "4.00": 1985.0,
    },
    "1500mm": {
      "1.00": 765.0,
      "1.50": 985.0,
      "2.00": 1225.0,
      "2.50": 1465.0,
      "3.00": 1705.0,
      "3.50": 1945.0,
      "4.00": 2185.0,
    },
    "1800mm": {
      "1.00": 925.0,
      "1.50": 1185.0,
      "2.00": 1465.0,
      "2.50": 1745.0,
      "3.00": 2025.0,
      "3.50": 2305.0,
      "4.00": 2585.0,
    },
    "2100mm": {
      "1.00": 1125.0,
      "1.50": 1425.0,
      "2.00": 1745.0,
      "2.50": 2065.0,
      "3.00": 2385.0,
      "3.50": 2705.0,
      "4.00": 3025.0,
    },
    "2400mm": {
      "1.00": 1385.0,
      "1.50": 1725.0,
      "2.00": 2085.0,
      "2.50": 2445.0,
      "3.00": 2805.0,
      "3.50": 3165.0,
      "4.00": 3525.0,
    },
  },

  // Road construction rates
  roads: {
    surfacing: {
      "HRA 55/10": 0.75, // per m² per mm thickness
      "SMA 10": 0.85,
      "DBM 10": 0.65,
    },
    baseCourse: 52.0, // per m³
    binderCourse: 58.0, // per m³
    subBase: {
      "Type 1": 18.5, // per m³
      "6F2": 12.2,
    },
    cappingLayer: {
      150: 15.8, // per m³
      350: 16.2,
      600: 16.8,
    },
    excavation: 12.5, // per m³
    cartAway: 18.0, // per m³
    topsoilStrip: 2.8, // per m²
    disposal: 8.5, // per m³
  },

  // Carriageway items
  carriageway: {
    kerbs: {
      precastConcrete: 32.5, // per linear meter
      naturalStone: 68.0,
      conservation: 95.0,
    },
    footpaths: {
      concrete: 28.5, // per m²
      tarmac: 22.0,
      block: 45.0,
    },
    crossovers: {
      standard: 285.0, // per unit
      heavy: 425.0,
    },
    roadMarkings: {
      whiteLine: 3.2, // per linear meter
      yellowLine: 3.8,
      hatching: 12.5, // per m²
    },
  },

  // Laterals and connections
  connections: {
    lateral: 125.0, // per connection
    gully: 185.0, // per gully
    roadGully: 225.0, // per road gully
    catchpit: 125.0, // per m³
    soakaway: 95.0, // per m³
  },

  // Additional Items rates
  additionalItems: {
    retainingWalls: {
      massConcreteWall: 185.0, // per m²
      reinforcedConcreteWall: 225.0, // per m²
      gabionWall: 95.0, // per m²
      steelSheetWall: 175.0, // per m²
      timberWall: 65.0, // per m²
      excavation: 12.5, // per m³
      drainageInstallation: 35.0, // per linear meter
      reinforcement: 1.85, // per kg
    },
    attenuationCrates: {
      polypipeCrates: 28.5, // per crate unit
      grafCrates: 32.0, // per crate unit
      aquacellCrates: 35.5, // per crate unit
      excavation: 12.5, // per m³
      geotextile: 4.2, // per m²
      stoneSurround: 25.0, // per m³
      pipework: 35.0, // per linear meter
      connections: 125.0, // per connection
    },
    compoundAreas: {
      stoneLayering: 18.5, // per m²
      concreteBase: 45.0, // per m²
      herasFencing: 12.5, // per linear meter per week
      timberFencing: 25.0, // per linear meter per week
      chainLinkFencing: 18.0, // per linear meter per week
      powerConnection: 450.0, // per connection
      waterConnection: 350.0, // per connection
      telecomConnection: 285.0, // per connection
      siteOffice: 125.0, // per week
      storageContainer: 85.0, // per week
      welfareUnit: 95.0, // per week
    },
    binStores: {
      concreteBase: 125.0, // per m²
      blockWalls: 85.0, // per m²
      roofing: 65.0, // per m²
      doors: 285.0, // per door
    },
    substationBases: {
      excavation: 15.0, // per m³
      concrete: 125.0, // per m³
      reinforcement: 1.85, // per kg
      formwork: 25.0, // per m²
      ducting: 35.0, // per linear meter
    },
    showHomePrep: {
      enhancedFinishes: 125.0, // per m²
      landscaping: 85.0, // per m²
      signage: 450.0, // per item
      tempServices: 285.0, // per connection
    },
    archaeologyWatching: {
      dailyRate: 350.0, // per day
      reportingRate: 125.0, // per report
      findHandling: 85.0, // per hour
    },
  },

  // Regional multipliers
  regional: {
    "West Midlands": { labour: 1.0, material: 1.0 },
    "East Midlands": { labour: 0.97, material: 0.98 },
    Southern: { labour: 1.1, material: 1.05 },
    Northwest: { labour: 0.93, material: 0.96 },
  } as Record<string, RegionalMultipliers>,
};

// Calculate plastic rates as 10% less than clay
Object.keys(rateLibrary.sewers.clay).forEach((diameter) => {
  rateLibrary.sewers.plastic[diameter] = {};
  Object.keys(rateLibrary.sewers.clay[diameter]).forEach((depth) => {
    const clayRates = rateLibrary.sewers.clay[diameter][depth];
    rateLibrary.sewers.plastic[diameter][depth] = {
      excavation: clayRates.excavation * 0.9,
      pipe: clayRates.pipe * 0.9,
      bedding: clayRates.bedding * 0.9,
      backfill: clayRates.backfill * 0.9,
    };
  });
});

// Calculate concrete rates as 15% more than clay
Object.keys(rateLibrary.sewers.clay).forEach((diameter) => {
  rateLibrary.sewers.concrete[diameter] = {};
  Object.keys(rateLibrary.sewers.clay[diameter]).forEach((depth) => {
    const clayRates = rateLibrary.sewers.clay[diameter][depth];
    rateLibrary.sewers.concrete[diameter][depth] = {
      excavation: clayRates.excavation * 1.15,
      pipe: clayRates.pipe * 1.15,
      bedding: clayRates.bedding * 1.15,
      backfill: clayRates.backfill * 1.15,
    };
  });
});

// Helper function to get pipe rates
export const getPipeRates = (
  pipeType: "clay" | "plastic" | "concrete",
  diameter: string,
  depthCategory: number,
  isStorm: boolean = false
): PipeRates | null => {
  const depth = depthCategory.toFixed(2);

  if (isStorm && parseInt(diameter) >= 750) {
    return (rateLibrary.stormSewers.plastic as any)[diameter]?.[depth] || null;
  }

  return (rateLibrary.sewers as any)[pipeType]?.[diameter]?.[depth] || null;
};

// Helper function to get manhole rates
export const getManholeRate = (
  diameter: string,
  depthCategory: number
): number => {
  const depth = depthCategory.toFixed(2);
  return (rateLibrary.manholes as any)[diameter]?.[depth] || 0;
};

// Helper function to get regional multipliers
export const getRegionalMultipliers = (region: string): RegionalMultipliers => {
  return rateLibrary.regional[region] || { labour: 1.0, material: 1.0 };
};
